/* File: /src/pages/ChatbotFaqAdmin.js
   Zweck:
   - FAQ-Verwaltung für ELENA mit EINER Tabelle (chatbot_faq)
   - Felder: lang, intent, question, answer, needs_review(1/0), usage_count, last_used
   - Features:
     • Filtern (Sprache/Intent/Review), Suchen (Frage/Antwort), Sortieren
     • Anlegen & Bearbeiten (inkl. needs_review)
     • Schnellaktion "Reviewed ✓" (needs_review -> 0)
     • Anzeige von Nutzung & "Zuletzt" (aus DB)
*/

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/config"; // deine Datei nutzt bereits diesen Named-Export. :contentReference[oaicite:1]{index=1}
import AdminPageFrame from "../components/admin/AdminPageFrame";

const INTENTS = [
  { value: "general", label: "Allgemein" },
  { value: "registration", label: "Registrierung" },
  { value: "visa", label: "Visum" },
  { value: "language", label: "Sprache" },
  { value: "recognition", label: "Anerkennung" },
  { value: "platform", label: "Plattform" },
];

const emptyForm = { lang: "de", intent: "general", question: "", answer: "", needs_review: 0 };

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
}

const ChatbotFaqAdmin = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Formular
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  // Filter & Suche
  const [search, setSearch] = useState("");
  const [langFilter, setLangFilter] = useState("all");
  const [intentFilter, setIntentFilter] = useState("all");
  const [onlyReview, setOnlyReview] = useState(false);

  // Sortierung
  const [sortBy, setSortBy] = useState("updated_at"); // oder: last_used | usage_count | id
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  const API = API_BASE_URL;

  // -----------------------
  // Daten laden
  // -----------------------
  const fetchFaqs = async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await axios.get(`${API}/api/chatbot-faq`);
      // Erwartete Felder (siehe Controller): id, lang, intent, question, answer, needs_review, usage_count, last_used, updated_at
      setFaqs(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Fehler beim Laden der FAQ:", e);
      setErr("Fehler beim Laden der FAQ.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // -----------------------
  // Hilfsfunktionen
  // -----------------------
  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = value;
    if (name === "needs_review") v = type === "checkbox" ? (checked ? 1 : 0) : Number(value) ? 1 : 0;
    setForm((prev) => ({ ...prev, [name]: v }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    // Pflichtfelder prüfen
    if (!form.question.trim() || !form.answer.trim()) {
      setErr("Bitte Frage und Antwort ausfüllen.");
      return;
    }
    const payload = {
      lang: (form.lang || "de").slice(0, 8),
      intent: form.intent || "general",
      question: form.question,
      answer: form.answer,
      needs_review: form.needs_review ? 1 : 0,
    };

    try {
      if (editingId) {
        await axios.put(`${API}/api/chatbot-faq/${editingId}`, payload);
      } else {
        await axios.post(`${API}/api/chatbot-faq`, payload);
      }
      await fetchFaqs();
      resetForm();
    } catch (e) {
      console.error("Fehler beim Speichern:", e);
      setErr("Fehler beim Speichern.");
    }
  };

  const handleEdit = (faq) => {
    // Nur erlaubte Felder ins Formular übernehmen
    setForm({
      lang: faq.lang || "de",
      intent: faq.intent || "general",
      question: faq.question || "",
      answer: faq.answer || "",
      needs_review: faq.needs_review ? 1 : 0,
    });
    setEditingId(faq.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/api/chatbot-faq/${id}`);
      await fetchFaqs();
      if (editingId === id) resetForm();
    } catch (e) {
      console.error("Fehler beim Löschen:", e);
      setErr("Fehler beim Löschen.");
    }
  };

  const toggleReviewed = async (faq) => {
    if (!faq?.id) return;
    try {
      await axios.put(`${API}/api/chatbot-faq/${faq.id}`, {
        lang: faq.lang,
        intent: faq.intent,
        question: faq.question,
        answer: faq.answer,
        needs_review: 0, // auf reviewed setzen
      });
      await fetchFaqs();
    } catch (e) {
      console.error("Fehler beim Markieren als reviewed:", e);
      setErr("Fehler beim Markieren als 'reviewed'.");
    }
  };

  // -----------------------
  // Filter / Suche / Sort
  // -----------------------
  const filteredSortedFaqs = useMemo(() => {
    let list = [...faqs];

    // Filter
    if (langFilter !== "all") list = list.filter((r) => r.lang === langFilter);
    if (intentFilter !== "all") list = list.filter((r) => r.intent === intentFilter);
    if (onlyReview) list = list.filter((r) => !!r.needs_review);

    // Suche (in Frage + Antwort)
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        (r.question || "").toLowerCase().includes(q) ||
        (r.answer || "").toLowerCase().includes(q)
      );
    }

    // Sort
    list.sort((a, b) => {
      const dir = sortDir === "asc" ? 1 : -1;
      const A = a[sortBy];
      const B = b[sortBy];
      if (A == null && B != null) return 1 * dir;
      if (A != null && B == null) return -1 * dir;
      if (sortBy === "usage_count" || sortBy === "id") {
        return ((Number(A) || 0) - (Number(B) || 0)) * dir;
      }
      // Strings/Datums
      const sa = String(A || "");
      const sb = String(B || "");
      return sa.localeCompare(sb) * dir;
    });

    return list;
  }, [faqs, langFilter, intentFilter, onlyReview, search, sortBy, sortDir]);

  const sortToggle = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("desc"); }
  };

  // -----------------------
  // Render
  // -----------------------
  return (
    <AdminPageFrame
      title="FAQ-Verwaltung"
      subtitle="Bekannte Elena-Antworten, Review-Status und Nutzung in einer operativen Tabelle."
      badge="FAQ Operations"
    >

      {/* Formular */}
      <form onSubmit={handleSubmit} className="bg-gray-800 p-4 rounded mb-6 space-y-4">
        {err && <div className="bg-red-900/60 border border-red-500 text-red-200 px-3 py-2 rounded">{err}</div>}

        <div className="flex flex-wrap gap-4">
          <select name="lang" value={form.lang} onChange={onChange} className="p-2 rounded bg-white text-black">
            <option value="de">Deutsch</option>
            <option value="en">Englisch</option>
            <option value="fr">Französisch</option>
            <option value="ar">Arabisch</option>
            <option value="es">Spanisch</option>
          </select>

          <select name="intent" value={form.intent} onChange={onChange} className="p-2 rounded bg-white text-black">
            {INTENTS.map((it) => (
              <option key={it.value} value={it.value}>{it.label}</option>
            ))}
          </select>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              name="needs_review"
              checked={!!form.needs_review}
              onChange={onChange}
              className="h-4 w-4"
            />
            <span>Markiert zur Prüfung</span>
          </label>
        </div>

        <input
          name="question"
          placeholder="Frage"
          value={form.question}
          onChange={onChange}
          className="w-full p-2 rounded text-black"
        />

        <textarea
          name="answer"
          placeholder="Antwort"
          value={form.answer}
          onChange={onChange}
          className="w-full p-2 rounded text-black"
          rows={4}
        />

        <div className="flex gap-3">
          <button
            type="submit"
            className="bg-cyan-600 hover:bg-cyan-500 px-4 py-2 rounded text-black font-bold"
            disabled={loading}
          >
            {editingId ? "Aktualisieren" : "Hinzufügen"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="bg-gray-600 hover:bg-gray-500 px-4 py-2 rounded text-white font-semibold"
            >
              Abbrechen
            </button>
          )}
        </div>
      </form>

      {/* Filterleiste */}
      <div className="bg-gray-900/60 p-3 rounded mb-3 flex flex-wrap items-center gap-3">
        <input
          placeholder="Suche in Frage/Antwort …"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="p-2 rounded text-black w-[280px]"
        />

        <select value={langFilter} onChange={(e) => setLangFilter(e.target.value)} className="p-2 rounded bg-white text-black">
          <option value="all">Alle Sprachen</option>
          <option value="de">Deutsch</option>
          <option value="en">Englisch</option>
          <option value="fr">Französisch</option>
          <option value="ar">Arabisch</option>
          <option value="es">Spanisch</option>
        </select>

        <select value={intentFilter} onChange={(e) => setIntentFilter(e.target.value)} className="p-2 rounded bg-white text-black">
          <option value="all">Alle Intents</option>
          {INTENTS.map((it) => <option key={it.value} value={it.value}>{it.label}</option>)}
        </select>

        <label className="flex items-center gap-2">
          <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} className="h-4 w-4" />
          <span>nur Review</span>
        </label>

        <div className="ml-auto flex items-center gap-2 text-sm">
          <span>Sortieren nach:</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="p-2 rounded bg-white text-black">
            <option value="updated_at">Aktualisiert</option>
            <option value="last_used">Zuletzt benutzt</option>
            <option value="usage_count">Nutzung</option>
            <option value="id">ID</option>
          </select>
          <button
            onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
            className="px-3 py-1 rounded bg-slate-700 hover:bg-slate-600"
            title="Richtung umschalten"
            type="button"
          >
            {sortDir === "asc" ? "▲" : "▼"}
          </button>
        </div>
      </div>

      {/* Tabelle */}
      <div className="overflow-x-auto">
        <table className="min-w-[920px] w-full text-sm text-left text-white border border-cyan-800">
          <thead className="bg-cyan-900 text-cyan-300 text-xs uppercase">
            <tr>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("lang")}>Sprache</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("intent")}>Intent</th>
              <th className="px-4 py-2">Frage</th>
              <th className="px-4 py-2">Antwort</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("needs_review")}>Review?</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("usage_count")}>Nutzung</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("last_used")}>Zuletzt</th>
              <th className="px-4 py-2 cursor-pointer" onClick={() => sortToggle("updated_at")}>Aktualisiert</th>
              <th className="px-4 py-2">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {filteredSortedFaqs.map((faq) => (
              <tr key={faq.id} className="border-t border-cyan-800 hover:bg-cyan-800/20 align-top">
                <td className="px-4 py-2">{faq.lang}</td>
                <td className="px-4 py-2">{faq.intent}</td>
                <td className="px-4 py-2 max-w-xs truncate" title={faq.question}>{faq.question}</td>
                <td className="px-4 py-2 max-w-md truncate" title={faq.answer}>{faq.answer}</td>
                <td className="px-4 py-2">{faq.needs_review ? "🟡" : "✅"}</td>
                <td className="px-4 py-2">{faq.usage_count ?? 0}</td>
                <td className="px-4 py-2">{formatDate(faq.last_used)}</td>
                <td className="px-4 py-2">{formatDate(faq.updated_at)}</td>
                <td className="px-4 py-2 space-x-3 whitespace-nowrap">
                  <button onClick={() => handleEdit(faq)} className="text-yellow-400 hover:underline">Bearbeiten</button>
                  <button onClick={() => handleDelete(faq.id)} className="text-red-400 hover:underline">Löschen</button>
                  {faq.needs_review ? (
                    <button onClick={() => toggleReviewed(faq)} className="text-emerald-400 hover:underline">Reviewed ✓</button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!loading && filteredSortedFaqs.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-6 text-center text-slate-300">Keine Einträge gefunden.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {loading && <div className="mt-3 text-sm text-slate-300">Lade...</div>}
    </AdminPageFrame>
  );
};

export default ChatbotFaqAdmin;
